/**
 * Resolve article image paths for local public assets or a future CDN/S3 base.
 *
 * Stored values can be:
 * - Absolute URLs: `https://…` (returned unchanged — useful once images live on S3)
 * - Site paths: `/article-images/lectures/lecture-1/a1.jpg`
 * - Relative keys: `lectures/lecture-1/a1.jpg`
 *
 * Set `VITE_ARTICLE_IMAGES_BASE_URL` to switch storage later without rewriting
 * every article path, e.g.:
 *   VITE_ARTICLE_IMAGES_BASE_URL=https://your-bucket.s3.amazonaws.com/article-images
 *
 * Default (local): `/article-images`
 */
const DEFAULT_BASE = '/article-images';

function getBase(): string {
  const raw = import.meta.env.VITE_ARTICLE_IMAGES_BASE_URL;
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim().replace(/\/$/, '');
  }
  return DEFAULT_BASE;
}

/** Normalize a stored image ref to a browser-ready URL. */
export function articleImageUrl(keyOrUrl: string | null | undefined): string {
  if (!keyOrUrl) return '';

  const value = keyOrUrl.trim();
  if (!value) return '';

  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) {
    return value;
  }

  const withoutLeadingSlash = value.replace(/^\/+/, '');
  const key = withoutLeadingSlash.replace(/^article-images\//, '');
  return `${getBase()}/${key}`;
}

/** Map a list of stored refs to resolved URLs. */
export function articleImageUrls(keysOrUrls: Array<string | null | undefined>): string[] {
  return keysOrUrls
    .map((item) => articleImageUrl(item ?? ''))
    .filter(Boolean);
}
